import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import { CheckboxField } from '../../CheckboxField/CheckboxField.tsx';
import { HighlightedPhrase } from '../../HighlightedPhrase/HighlightedPhrase.tsx';
import type { ListItemProps } from '../../List/types.ts';
import { ValuePlaceholder } from '../../Value/ValuePlaceholder/ValuePlaceholder.tsx';
import { DEFAULT_TRANSFER_LIST_ITEM_SIZE } from '../constants.ts';
import type { TransferListBaseItem, TransferListItemProps } from '../types.ts';
import { ConditionalTooltip } from './ConditionalTooltip.tsx';

export const TransferListItem = <TItem extends TransferListBaseItem>({
  fields,
  index,
  isBlocked,
  isDisabled,
  item,
  itemsSelected,
  onSelect,
  searchPhrase,
  setItemSize,
  style,
  windowWidth,
}: ListItemProps<TItem, TransferListItemProps<TItem>>) => {
  const itemRef = useRef<HTMLLIElement | null>(null);

  const hasCheckbox = !!onSelect;
  const { isItemBlocked, tooltipOnBlocked } = useMemo(() => {
    const blockedInfo = isBlocked?.(item);

    return {
      isItemBlocked: !!blockedInfo,
      tooltipOnBlocked: typeof blockedInfo === 'string' ? blockedInfo : '',
    };
  }, [item, isBlocked]);

  const dataTestSuffix = isItemBlocked ? 'inactive' : 'active';

  // biome-ignore lint/correctness/useExhaustiveDependencies: item size should be updated when widow width is changed
  useEffect(() => {
    setItemSize(index, itemRef.current?.clientHeight || DEFAULT_TRANSFER_LIST_ITEM_SIZE);
  }, [index, setItemSize, windowWidth]);

  return (
    <Box style={style}>
      <ListItem
        data-test={`transfer-list-item-${dataTestSuffix}`}
        ref={itemRef}
        sx={{ opacity: isDisabled || isItemBlocked ? 0.38 : 1 }}
      >
        <Grid
          container
          sx={{
            flexGrow: 1,
          }}
        >
          {hasCheckbox && (
            <Grid size={1}>
              <ConditionalTooltip hasTooltip={isItemBlocked} title={tooltipOnBlocked}>
                <CheckboxField
                  checked={item.id in itemsSelected}
                  disabled={isDisabled || isItemBlocked}
                  onChange={event => onSelect?.(item, event.target.checked)}
                />
              </ConditionalTooltip>
            </Grid>
          )}

          <Grid size={hasCheckbox ? 11 : 12}>
            {item.name ? (
              <Tooltip title={item.name}>
                <Typography
                  variant="body1Bold"
                  sx={{
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: 'fit-content',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <HighlightedPhrase searchPhrase={searchPhrase}>{item.name}</HighlightedPhrase>
                </Typography>
              </Tooltip>
            ) : (
              <ValuePlaceholder />
            )}

            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                flexWrap: 'wrap',
                columnGap: 1.5,
              }}
            >
              {fields.map(
                ({ id, isHidden, render }) => !isHidden && <Fragment key={id}>{render?.(item, searchPhrase)}</Fragment>
              )}
            </Stack>
          </Grid>
        </Grid>
      </ListItem>
    </Box>
  );
};
